import { ChangeEvent, useEffect, useState } from 'react';
import { Text, Flex, useToast, Skeleton, Switch } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { PageContent } from 'src/components/PageContent';
import { SectionBox } from 'src/components/SectionBox';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { breadcrumb } from './breadcrumb';
import { ApplicationComponentId } from '@/model/application-component';
import { useAppApplicationComponentArea, useEnableApplicationComponentMutation } from '@/services/queries/admin-config';
import { ConfirmationModal } from '@/components/ConfirmationModal';
import { getConfigFromStorage } from '@/utils/config';
import { AnthemMultiTeamView } from './components/AnthemMultiTeamView';
import { AnthemSingleTeamForm } from './components/AnthemSingleTeamForm';

export const ApplicationHistoryAnthem = () => {
  const { t } = useTranslation();
  const toast = useToast();

  const isMultiTeam = getConfigFromStorage()?.is_multi_team ?? true;

  const [isAppAreaActive, setIsAppAreaActive] = useState(false);
  const { data: appAreaData, isLoading: isLoadingAppArea } = useAppApplicationComponentArea(ApplicationComponentId.HISTORY);
  const { mutate: enableAppAreaMutation, isLoading: isLoadingEnableOrDisableAppArea } = useEnableApplicationComponentMutation();
  const [pendingAppAreaState, setPendingAppAreaState] = useState<boolean | null>(null);
  const [isDialogAppAreaState, setIsDialogAppAreaState] = useState(false);

  const cancelAppAreaChange = () => {
    setIsDialogAppAreaState(false);
    setPendingAppAreaState(null);
  };

  const handleAppAreaChange = (event: ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    setPendingAppAreaState(isChecked);
    setIsDialogAppAreaState(true);
  };

  const confirmAppAreaChange = () => {
    if (pendingAppAreaState === null) return;

    const successActionString = pendingAppAreaState
      ? t('pages.application_history_anthem.application_history_anthem_enable_success')
      : t('pages.application_history_anthem.application_history_anthem_disable_success');
    const errorActionString = pendingAppAreaState
      ? t('pages.application_history_anthem.application_history_anthem_enable_error')
      : t('pages.application_history_anthem.application_history_anthem_disable_error');

    setIsAppAreaActive(pendingAppAreaState);

    enableAppAreaMutation([{ component_id: ApplicationComponentId.HISTORY, active: pendingAppAreaState }], {
      onSuccess: () => {
        toast({ status: 'success', title: successActionString });
      },
      onError: () => {
        toast({ status: 'error', title: errorActionString });
        setIsAppAreaActive(!pendingAppAreaState);
      },
    });

    setIsDialogAppAreaState(false);
    setPendingAppAreaState(null);
  };

  useEffect(() => {
    if (!appAreaData) return;
    setIsAppAreaActive(appAreaData.is_active);
  }, [appAreaData]);

  return (
    <PageContent title={t('pages.application_history_anthem.application_history_anthem_title')} breadcrumbItems={breadcrumb}>
      <SectionBox bodyProps={{ gap: 'small' }}>
        <Flex alignItems='center' gap={4}>
          {isLoadingAppArea ? (
            <>
              <Skeleton w='130px' h='21px' />
              <Skeleton w='102px' h='21px' />
            </>
          ) : (
            <>
              <Text fontWeight='bold' color='brand.500'>
                {t('pages.application_history_anthem.application_history_anthem_title')}
              </Text>
              <Switch size='sm' isChecked={isAppAreaActive} onChange={handleAppAreaChange}>
                {isAppAreaActive
                  ? t('pages.application_history_anthem.application_history_anthem_enabled')
                  : t('pages.application_history_anthem.application_history_anthem_disabled')}
              </Switch>
            </>
          )}
        </Flex>

        {isMultiTeam ? <AnthemMultiTeamView /> : <AnthemSingleTeamForm />}
      </SectionBox>

      <ConfirmationModal
        isOpen={isDialogAppAreaState}
        onClose={cancelAppAreaChange}
        onConfirm={confirmAppAreaChange}
        title={t('pages.application_history_anthem.application_history_anthem_toggle_confirm_title')}
        description={
          <Text>
            {pendingAppAreaState
              ? t('pages.application_history_anthem.application_history_anthem_enable_confirm_description')
              : t('pages.application_history_anthem.application_history_anthem_disable_confirm_description')}
          </Text>
        }
      />

      <CenteredSpinner isLoading={isLoadingEnableOrDisableAppArea} />
    </PageContent>
  );
};
